import { makeStyles } from "tss-react/mui"
import {
  alpha,
  Breakpoint,
  buttonBaseClasses,
  menuItemClasses,
  tableCellClasses
} from "@mui/material"

export type StylesParams = {
  size?: "sm" | "lg"
  loading?: boolean
}

const DIAGONAL_HEADER_HEIGHT = 100

export type StylesClasses =
  | "container"
  | "tableWrapper"
  | "tableWrapperLoading"
  | "table"
  | "head"
  | "headerSticky"
  | "headerContainer"
  | "headRow"
  | "body"
  | "row"
  | "rowNoHover"
  | "rowEven"
  | "selectedRow"
  | "clickableRow"
  | "tableCell"
  | "headTableCell"
  | "bodyTableCell"
  | "bodyTableCellWithDiagonalHeaders"
  | "checkboxCell"
  | "actionCell"
  | "expandableCell"
  | "expandButton"
  | "expandedCell"
  | "sortable"
  | "sortButton"
  | "align_left"
  | "align_right"
  | "align_center"
  | "sortIcon"
  | "activeSortIcon"
  | "checkbox"
  | "configDropdown"
  | "emptyPlaceholder"
  | "footer"
  | "headRowGroup"
  | "groupedCell"
  | "groupCell"
  | "diagonalHeaderCell"
  | "diagonalHeaderInner"
  | "diagonalHeaderText"
  | "headTableCellDiagonal"
  | "bodyTableCellDiagonal"
  | "actionCellDiagonal"
  | "diagonalHeaderLabel"
  | "diagonalHeaderSortIcon"
  | "firstColumn"
  | "spacerCell"
  | "headerStickyWithDiagonal"
  | "firstDiagonalColumn"
  | "tableCellDiagonal"
  | "inheritWidth"

export const useStyles = makeStyles<StylesParams, StylesClasses>()(
  (
    { spacing, palette, breakpoints },
    { size = "lg", loading = false },
    classes
  ) => ({
    container: {
      display: "flex",
      flexDirection: "column",
      height: "100%"
    },
    tableWrapper: {
      overflowX: loading ? "hidden" : "auto",
      overflowY: "visible",
      height: "100%",
      scrollbarWidth: "none",
      scrollbarColor: "none",
      position: "relative",

      "::-webkit-scrollbar": {
        width: "18px",
        height: "18px",
        padding: spacing(1),

        [breakpoints.down("sm" as Breakpoint)]: {
          width: "13px",
          height: "13px"
        }
      },
      "::-webkit-scrollbar-thumb": {
        border: "4px solid transparent",
        borderRadius: "10px",
        backgroundClip: "padding-box"
      }
    },
    tableWrapperLoading: {
      position: "relative",
      height: "100%"
    },
    table: {
      borderCollapse: "separate",
      display: "grid"
    },
    head: {
      display: "grid"
    },
    headerSticky: {
      minHeight: "46px",
      position: "sticky",
      zIndex: 900,
      overflowX: "auto",
      overflowY: "hidden",
      scrollbarWidth: "auto",
      scrollbarColor: "auto"
    },
    headerStickyWithDiagonal: {
      minHeight: `${DIAGONAL_HEADER_HEIGHT}px`
    },
    headerContainer: {
      width: "100%",
      position: "relative"
    },
    headRow: {
      display: "flex",
      minWidth: "100%",
      width: "max-content"
    },
    headRowGroup: {
      [`& .${classes.headTableCell}`]: {
        background: palette.common.white,
        borderBottom: "unset",
        minHeight: "26px",
        height: "26px",
        padding: spacing(0, 2),

        "&:hover": {
          background: palette.common.white
        }
      }
    },
    groupedCell: {
      borderRight: `2px solid ${palette.primary[100]}`,

      [`&:has(+ .${classes.actionCell})`]: {
        borderRight: "none"
      }
    },
    groupCell: {
      borderRight: `1px solid ${palette.primary[100]}`
    },
    body: {
      display: "grid",
      position: "relative"
    },
    inheritWidth: {
      width: "inherit"
    },
    row: {
      display: "flex",
      left: 0,
      minWidth: "100%",
      width: "max-content",

      [`&:hover .${tableCellClasses.root}`]: {
        backgroundColor: palette.primary[50]
      }
    },
    rowNoHover: {
      [`&:hover .${tableCellClasses.root}`]: {
        backgroundColor: "inherit"
      }
    },
    rowEven: {
      [`& .${classes.bodyTableCell}, & .${classes.spacerCell}`]: {
        // Blend half-opaque color with white bg to cover underlying content on scroll.
        backgroundColor: alpha(palette.primary[50], 0.5),
        backgroundImage: `linear-gradient(to right, ${palette.background.paper}, ${palette.background.paper})`,
        backgroundBlendMode: "multiply"
      }
    },
    selectedRow: {
      [`&:nth-of-type(n) .${tableCellClasses.root}, & .${classes.spacerCell}`]:
        {
          backgroundColor: palette.secondary[50]
        }
    },
    clickableRow: {
      cursor: "pointer"
    },
    tableCell: {
      border: "none",
      padding: `15px ${spacing(2)}`,
      minHeight: "48px",
      // whiteSpace: "nowrap",
      backgroundColor: palette.background.paper,
      display: "flex",

      ...(size === "sm" && {
        padding: `10px ${spacing(2)}`,
        minHeight: "38px"
      })
    },
    tableCellDiagonal: {
      minHeight: "38px",
      height: "38px",
      width: "100%",
      alignSelf: "flex-end",
      borderRight: `2px solid ${palette.primary[100]}`,
      padding: `${spacing(2)} 10px`
    },
    headTableCellDiagonal: {
      background: palette.common.white,
      alignSelf: "flex-end",
      minHeight: "38px",
      height: "100%",
      padding: 0
    },
    firstDiagonalColumn: {
      borderRight: `1px solid ${palette.primary[500]}`
    },
    headTableCell: {
      borderBottom: `2px solid ${palette.primary[100]}`,
      userSelect: "none",
      // Blend half-opaque color with white bg to cover underlying content on scroll.
      backgroundColor: alpha(palette.primary[50], 0.5),
      backgroundImage: `linear-gradient(to right, ${palette.background.paper}, ${palette.background.paper})`,
      backgroundBlendMode: "multiply",
      display: "flex",
      flex: "1 0 auto",

      "&:hover": {
        backgroundColor: alpha(palette.primary[50], 1)
      },

      [`&:hover .${classes.sortIcon}`]: {
        color: palette.primary[500]
      },

      [`&.${classes.checkboxCell}`]: {
        position: "sticky",
        // Need to be greater than "zIndex" of sticky header cell.
        zIndex: 3
      },

      [`&.${classes.actionCell}`]: {
        position: "sticky",
        zIndex: 2,

        [breakpoints.down("sm" as Breakpoint)]: {
          zIndex: 0
        }
      },
      [`&.${classes.firstColumn}`]: {
        position: "sticky",
        zIndex: 4,
        borderBottom: `2px solid ${palette.primary[100]}`,

        [breakpoints.down("sm" as Breakpoint)]: {
          position: "static",
          left: "initial"
        }
      }
    },
    firstColumn: {
      position: "sticky",
      left: 0,
      zIndex: 4,
      borderRight: `2px solid ${palette.primary[100]}`,

      [breakpoints.down("sm" as Breakpoint)]: {
        position: "static",
        left: "initial"
      }
    },
    bodyTableCell: {
      flex: "1 0 auto",
      display: "flex",
      alignItems: "center",

      "& p": {
        wordBreak: "break-word",
        width: "100%"
      },

      // Apply alignment styles
      "&.align_left": {
        justifyContent: "flex-start",
        textAlign: "left"
      },
      "&.align_center": {
        justifyContent: "center",
        textAlign: "center",
        "& > *": {
          textAlign: "center",
          width: "100%"
        }
      },
      "&.align_right": {
        justifyContent: "flex-end",
        textAlign: "right",
        "& > *": {
          textAlign: "right"
        }
      },
      [`&.${classes.firstColumn}`]: {
        borderBottom: "none"
      }
    },
    bodyTableCellDiagonal: {
      borderRight: `2px solid ${palette.primary[100]}`
    },
    bodyTableCellWithDiagonalHeaders: {
      borderRight: `1px solid ${palette.primary[100]}`,

      [`&:not(.${classes.actionCell}):not(.${classes.checkboxCell})`]: {
        [`&:last-child, &:has(+ .${classes.actionCell}), &:has(+ .${classes.spacerCell})`]:
          {
            borderRight: "none"
          }
      },

      // Remove border for action cell and checkbox cell
      [`&.${classes.actionCell}, &.${classes.checkboxCell}`]: {
        borderRight: "none"
      },

      // Remove border for grouped cells (they have their own thicker border)
      [`&.${classes.groupedCell}, &.${classes.groupCell}`]: {
        borderRight: "none"
      }
    },
    checkboxCell: {
      padding: spacing(4, 3),
      width: "40px",
      flex: "0 0 auto",
      position: "sticky",
      left: 0,
      zIndex: 2,
      borderRight: `2px solid ${palette.primary[100]}`,

      ...(size === "sm" && {
        padding: `11px ${spacing(3)}`
      }),

      [breakpoints.down("sm" as Breakpoint)]: {
        position: "static",
        left: "initial",
        borderRight: "none"
      }
    },
    actionCell: {
      padding: spacing(1),
      width: "38px",
      position: "sticky",
      right: 0,
      borderLeft: `2px solid ${palette.primary[100]}`,
      display: "flex",
      alignItems: "center",
      flex: "none",

      [breakpoints.down("sm" as Breakpoint)]: {
        position: "static",
        right: "initial",
        borderLeft: "none"
      }
    },
    actionCellDiagonal: {
      alignItems: "flex-end",
      background: palette.common.white,
      borderLeft: "unset",
      paddingLeft: 0,
      paddingBottom: 0,

      ["& div"]: {
        borderLeft: `2px solid ${palette.primary[100]}`,
        height: "38px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }
    },
    spacerCell: {
      width: "54px",
      minWidth: "20px",
      flex: "0 0 auto",
      padding: 0,
      border: "none",
      backgroundColor: palette.background.paper,

      "&.header-cell": {
        borderBottom: `2px solid ${palette.primary[100]}`
      }
    },
    expandableCell: {
      gap: spacing(1)
    },
    expandButton: {
      margin: "-6px 0",
      alignSelf: "center"
    },
    expandedCell: {
      paddingLeft: "42px"
    },
    sortable: {
      padding: 0
    },
    sortButton: {
      border: "none",
      backgroundColor: "transparent",
      width: "100%",
      height: "100%",

      // Outer box-shadow doesn't look good on sticky elements.
      ":focus, :focus-visible": {
        outline: `1px solid ${palette.focus}`,
        outlineOffset: "-3px",
        boxShadow: `inset 0 0 0 3px ${alpha(palette.focus, 0.2)} !important`
      }
    },
    align_left: {
      textAlign: "start",
      justifyContent: "start",
      // for sortable columns need to align internal button
      [`& ${buttonBaseClasses.root}`]: {
        textAlign: "start",
        justifyContent: "start"
      }
    },
    align_right: {
      textAlign: "end",
      justifyContent: "flex-end",
      [`& ${buttonBaseClasses.root}`]: {
        textAlign: "end",
        justifyContent: "flex-end"
      }
    },
    align_center: {
      textAlign: "center",
      justifyContent: "center",
      [`& ${buttonBaseClasses.root}`]: {
        textAlign: "center",
        justifyContent: "center"
      }
    },
    sortIcon: {
      fontSize: "0.75rem",
      color: palette.primary[400],
      cursor: "pointer"
    },
    activeSortIcon: {
      color: palette.primary[800]
    },
    checkbox: {
      padding: spacing(0),
      display: "flex",
      width: "1rem",
      height: "1rem"
    },
    configDropdown: {
      display: "flex",

      [`& .${menuItemClasses.root}`]: {
        paddingRight: spacing(3)
      }
    },
    emptyPlaceholder: {
      color: palette.primary[600],
      justifyContent: "center",
      padding: spacing(3)
    },
    footer: {
      padding: spacing(3),
      display: "flex",
      justifyContent: "end",

      [breakpoints.down("sm" as Breakpoint)]: {
        padding: spacing(2, 3)
      }
    },
    diagonalHeaderCell: {
      display: "block",
      height: `${DIAGONAL_HEADER_HEIGHT}px`,
      padding: spacing(3),
      whiteSpace: "nowrap",
      // Override flex behavior from headTableCell to maintain fixed width
      // but allow it to grow with table size like body cells
      minWidth: "60px",
      borderBottom: `2px solid ${palette.primary[100]}`,

      "&:hover": {
        backgroundColor: "unset"
      },

      [`&:last-child, &:has(+ .${classes.actionCell}), &:has(+ .${classes.spacerCell})`]:
        {
          [`& .${classes.diagonalHeaderText}::after`]: {
            borderBottom: "none"
          }
        }
    },
    diagonalHeaderInner: {
      transform: "translate(32px, 50px) rotate(305deg)",
      [breakpoints.down("md" as Breakpoint)]: {
        transform: "translate(28px, 50px) rotate(310deg)"
      }
    },
    diagonalHeaderText: {
      position: "relative",
      padding: spacing(2, 0),
      display: "inline-flex",
      alignItems: "center",
      maxWidth: "114px",
      minWidth: "114px",
      userSelect: "none",

      "&::after": {
        content: '""',
        position: "absolute",
        left: 0,
        bottom: 0,
        width: "100px",
        borderBottom: `1px solid ${palette.primary[100]}`
      },

      "&.first-diagonal-column:after": {
        borderBottom: `1px solid ${palette.primary[500]}`
      }
    },
    diagonalHeaderLabel: {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      maxWidth: "calc(100% - 20px)",
      flexShrink: 1
    },
    diagonalHeaderSortIcon: {
      display: "inline-block",
      marginLeft: "4px",
      verticalAlign: "middle",
      flexShrink: 0,
      minWidth: "16px"
    }
  })
)
